import React, { FC, HTMLAttributes, useState, useCallback, SyntheticEvent } from 'react';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import debounce from 'lodash/debounce';

import { INotificationUserEmployeeDTO } from 'src/api/dtos';
import UsersRepository from 'src/api/repositories/common/v1/users.repository';
import {
  formatUserEmployeeInitials,
  formatUserEmployeeName,
  VISIBLE_RECIPIENTS_COUNT,
} from 'src/features/message-center/MessageCenter.utils';
import { CloseIcon, SearchBar } from 'src/shared/components';
import { useInfiniteScroll } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { handleScrollEnd } from 'src/shared/utils';

const SCROLL_OFFSET = 100;

interface Props {
  recipients: INotificationUserEmployeeDTO[];
  recipientsCount: number;
  loadingRecipients: boolean;
  userIds: number[];
  excludeUserIds: number[];
  onUserIdsChange: (userIds: number[]) => void;
  onExcludeUserIdsChange: (excludeUserIds: number[]) => void;
  onLoadMoreRecipients: () => void;
}

export const MessageCenterDetailsToInput: FC<Props> = ({
  recipients,
  recipientsCount,
  loadingRecipients,
  userIds,
  excludeUserIds,
  onUserIdsChange,
  onExcludeUserIdsChange,
  onLoadMoreRecipients,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    collection: users,
    handleLoadMore: handleLoadMoreUsers,
    isLoadingMore,
    isRefreshing,
  } = useInfiniteScroll<INotificationUserEmployeeDTO>({
    useQuery: UsersRepository.endpoints.useGetUsersQuery,
    apiParams: { perPage: 20, searchTerm },
    skip: !open,
  });

  const loadingUsers = isLoadingMore || isRefreshing;

  const debounceSearchTermChange = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    [],
  );

  const handleInputValueChange = (_: SyntheticEvent, value: string) => {
    debounceSearchTermChange(value);
    setInputValue(value);
  };

  const handleOpenUsers = () => {
    setOpen(true);
  };

  const handleCloseUsers = () => {
    setInputValue('');
    setSearchTerm('');
    setOpen(false);
  };

  const handleUserSelect = (user: INotificationUserEmployeeDTO) => {
    if (userIds.includes(user.id)) {
      onUserIdsChange(userIds.filter((id) => id !== user.id));
    } else {
      onUserIdsChange([...userIds, user.id]);
      // When adding to 'userIds', remove from 'excludeUserIds'
      onExcludeUserIdsChange(excludeUserIds.filter((id) => id !== user.id));
    }
  };

  const renderUserOption = (props: HTMLAttributes<HTMLLIElement>, option: INotificationUserEmployeeDTO) => {
    const checked = userIds.includes(option.id);

    return <UserOption {...props} userEmployee={option} checked={checked} onSelect={handleUserSelect} />;
  };

  const handleOpenRecipients = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseRecipients = () => {
    setAnchorEl(null);
  };

  const handleRemoveRecipient = (recipient: INotificationUserEmployeeDTO) => {
    onExcludeUserIdsChange([...excludeUserIds, recipient.id]);
    // When adding to 'excludeUserIds', remove from 'userIds'
    onUserIdsChange(userIds.filter((id) => id !== recipient.id));
  };

  return (
    <SearchBar
      // Open
      open={open}
      onOpen={handleOpenUsers}
      onClose={handleCloseUsers}
      // Options
      multiple
      value={recipients.slice(0, VISIBLE_RECIPIENTS_COUNT)}
      options={users}
      getOptionLabel={(option) => `${option.id}`}
      renderOption={renderUserOption}
      filterOptions={(options) => options} // Don't filter options with inputValue
      // Loading
      loading={loadingRecipients || loadingUsers}
      // Input
      inputValue={inputValue}
      onInputChange={handleInputValueChange}
      // Tags
      renderTags={(value, getTagProps) => {
        return (
          <>
            {value.map((option, index) => (
              // Eslint complains about missing key prop but is provided from getTagProps
              // eslint-disable-next-line react/jsx-key
              <StyledChip
                label={formatUserEmployeeName(option)}
                {...getTagProps({ index })}
                onDelete={() => handleRemoveRecipient(option)}
                key={index}
                deleteIcon={<CloseIcon color={theme.palette.text.primary} />}
              />
            ))}
            {recipientsCount > VISIBLE_RECIPIENTS_COUNT && (
              <>
                <StyledChip onClick={handleOpenRecipients} label={`+${recipientsCount - VISIBLE_RECIPIENTS_COUNT}`} />
                <MessageCenterDetailsToInputRecipients
                  anchorEl={anchorEl}
                  onClose={handleCloseRecipients}
                  recipients={recipients.slice(VISIBLE_RECIPIENTS_COUNT, recipients.length)}
                  loading={loadingRecipients}
                  onLoadMore={onLoadMoreRecipients}
                  onRemove={handleRemoveRecipient}
                />
              </>
            )}
          </>
        );
      }}
      // Props
      slotProps={{
        paper: {
          onMouseDown: (event) => {
            event.stopPropagation();
            event.preventDefault();
          },
        },
      }}
      ListboxProps={{
        onScroll: (event) =>
          handleScrollEnd(event, handleLoadMoreUsers, { loading: loadingUsers, scrollOffset: SCROLL_OFFSET }),
        role: 'list-box',
      }}
      // Settings
      placeholder={t('messageCenter.selectRecipients')}
      disableClearable
      disableCloseOnSelect
      // Styles
      fullWidth
      forcePopupIcon={false}
    />
  );
};

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: '50px',
  background: `${theme.palette.action.selected} !important`,
  span: { fontSize: '0.875rem', lineHeight: '0.875rem' },
  svg: { width: '20px !important' },
}));

interface UserOptionProps {
  userEmployee: INotificationUserEmployeeDTO;
  checked: boolean;
  onSelect: (employee: INotificationUserEmployeeDTO) => void;
}

const UserOption: FC<UserOptionProps> = ({ userEmployee, checked, onSelect }) => {
  return (
    <ListItem key={userEmployee.id} sx={{ p: 0, m: 0 }}>
      <FormControlLabel
        onClick={(event) => {
          onSelect(userEmployee);
          event.stopPropagation();
          event.preventDefault();
        }}
        control={<Checkbox size="small" checked={checked} />}
        label={
          <Stack direction="row" gap={2} alignItems="center" sx={{ wordWrap: 'break-word' }}>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              flexShrink={0}
              width={40}
              height={40}
              borderRadius="50%"
              color={(theme) =>
                theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.primary.main
              }
              sx={{
                background: (theme) => theme.palette.secondary.main,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {formatUserEmployeeInitials(userEmployee)}
            </Stack>
            {formatUserEmployeeName(userEmployee)}
          </Stack>
        }
        sx={{
          width: '100%',
          flex: 1,
          margin: '8px 0',
          padding: '0 7px',
          '.MuiFormControlLabel-label': {
            fontSize: '0.875rem',
          },
        }}
      />
    </ListItem>
  );
};

const MessageCenterDetailsToInputRecipients: FC<{
  anchorEl: null | HTMLElement;
  onClose: () => void;
  recipients: INotificationUserEmployeeDTO[];
  loading: boolean;
  onLoadMore: () => void;
  onRemove: (recipient: INotificationUserEmployeeDTO) => void;
}> = ({ anchorEl, onClose, recipients, loading, onLoadMore, onRemove }) => {
  const open = Boolean(anchorEl);

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: { width: '100%', maxWidth: 300, maxHeight: 200, overflow: 'auto' },
          onScroll: (event) => handleScrollEnd(event, onLoadMore, { loading, scrollOffset: SCROLL_OFFSET }),
        },
      }}
      sx={{ mt: 1 }}
    >
      <List>
        {recipients.map((recipient) => (
          <ListItem key={recipient.id} sx={{ p: '8px 16px' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flex={1}>
              <Typography variant="body2">{formatUserEmployeeName(recipient)}</Typography>
              <IconButton size="small" onClick={() => onRemove(recipient)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </ListItem>
        ))}
      </List>
    </Popover>
  );
};
